<template>
  <fts-client-modal
    :footer="isFooter"
    append-content-class="workspace-modal__body"
    title="Workspace Settings"
    class="fts-workspace-modal"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['fas', 'cog']" fixed-width />
    <!-- body -->
    <template #body>
      <div class="client-modal-spinner" v-if="loading">
        <fts-spinner show />
      </div>
      <template v-else>
        <div class="user-profile-tabs">
          <div
            v-for="x in sections"
            :key="x.id"
            class="user-profile-tab"
            :class="{ 'active': section === x.id }"    
            @click="section = x.id"
          >
            {{ x.name }}
          </div>
        </div>
        <!-- company section -->
        <div class="fts-row gutters" v-if="section === 'company'">
          <div class="fts-col-md-5 comfort-form-design">
            <div class="fts-col fts-form-group">
              <div class="profile-photo">
                <div class="profile-photo-container">
                  <div class="profile-photo-wrapper" id="pick-avatar">
                    <img :src="profilePhoto" />
                  </div>
                  <div class="profile-photo-icon">
                    <fts-btn
                      sm
                      icon
                      color="primary"
                      box-shadow-none
                      :submitting="uploaderUploading"
                      @click="openUploader"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" />
                    </fts-btn>
                  </div>
                  <div class="profile-photo-icon">
                    <fts-btn
                      sm
                      icon
                      box-shadow-none
                      :disabled="uploaderUploading"
                      @click="removeUploadPicture"
                      v-if="formData.companyLogo"
                    >
                      <font-awesome-icon :icon="['fas', 'times']" />
                    </fts-btn>
                  </div>
                </div>
              </div>
            </div>

            <avatar-cropper
              trigger="#pick-avatar"
              :upload-url="fileUploadURL"
              :upload-form-data="{ workspaceId: workspaceId }"
              :labels="{ submit: 'Done', cancel: 'Cancel' }"
              :output-options="{
                width: 512,
                height: 512,
              }"
              ref="avatarCropper"
              :cropper-options="{ aspectRatio: null }"
              @uploading="uploaderUploadingHandler"
              @completed="uploaderCompletedHandler"
              @error="uploaderErrorHandler"
              @uploaded="uploaderUploadedHandler"
            />
          </div>

          <div class="fts-col-md-7">
            <fts-form-element
              v-for="x in fields"
              :item="x"
              :formData="formData"
              :validator="$v.formData"
              :key="x.id"
              comfortDesign
            />
          </div>
        </div>
        <!-- subscription section -->
        <div v-if="section === 'subscription'">
          <div class="workspace-modal-form-label plan-description">
            Please subscribe by clicking plan below. Unleash the power of <span class="fts-font-bold">Inistate</span> today!
          </div>

          <div class="plan-wrapper">
            <div class="plan-info">
              <div class="plan-name">Standard</div>
              <div class="plan-price">
                <div class="plan-price-tag">$828.00</div>
                <div class="plan-price-label">USD / year</div>
              </div>
            </div>
            <fts-btn
              tag="a"
              color="primary"
              box-shadow-none
              text-transform-init
              href="https://buy.stripe.com/7sI6qG6Okb586c0aEF"
            >Subscribe</fts-btn>
          </div>

          <div class="plan-wrapper">
            <div class="plan-info">
              <div class="plan-name">Standard</div>
              <div class="plan-price">
                <div class="plan-price-tag">$79.00</div>
                <div class="plan-price-label">USD / month</div>
              </div>
            </div>
            <fts-btn
              tag="a"
              color="primary"
              box-shadow-none
              text-transform-init
              href="https://buy.stripe.com/aEU6qG5Kgehk0RG9AC"
            >Subscribe</fts-btn>
          </div>
        </div>
        <!-- workspace section -->
        <div class="workspace-section" v-else-if="section === 'workspace'">
          <div class="workspace-modal-form-title workspace-modal-form-title--mb">
            Workspace
          </div>
          <fts-form-element
            v-for="x in workspaceFields"
            :item="x"
            :form-data="formData"
            :validator="$v.formData"
            :key="x.id"
            comfortDesign
          >
            <!-- workspace name format error -->
            <div
              slot="errorMessage"
              v-if="
                x.fieldName === 'name' &&
                $v.formData.name &&
                $v.formData.name.format === false
              "
            >
              <div class="fts-text-default">Allow alphabet and numeric only.</div>
              <div class="fts-text-default">
                Allow special characters
                <span
                  v-for="(x, ind) in workspaceNameSpecialCharacters"
                  :key="`wnsc-${ind}`"
                  class="fts-font-bold"
                >{{x}}</span>
              </div>
              <div>Workspace name cannot contain invalid characters.</div>
            </div>
          </fts-form-element>
          <!-- email -->
          <fts-collapsible-element
            class="workspace-modal-collapsible"
            v-if="isInternal"
          >
            <div
              slot="toggle"
              slot-scope="{ toggle, isVisible }"
              class="workspace-modal-collapsible__toggle"
              :class="{'workspace-modal-collapsible__toggle--expand': isVisible}"
              @click="toggle"
            >
              Email
              <font-awesome-icon
                :icon="['fas', 'caret-right']"
                class="workspace-modal-collapsible__toggle-icon"
              /> 
            </div>
            <fts-form-element
              slot="content"
              v-for="x in emailFields"
              :item="x"
              :form-data="formData"
              :validator="$v.formData"
              :key="x.id"
              comfort-design
              class="workspace-modal-collapsible__content"
            />
          </fts-collapsible-element>
        </div>
        <!-- app section -->
        <div class="app-section" v-else-if="section === 'app'">
          <div class="workspace-modal-form-label">
            Just go to Apple App Store or Google Play Store to download and install.
          </div>
          <div class="workspace-modal-form-label">
            Search for <span class="fts-font-bold">Inistate</span>, install and login with your account.
          </div>

          <div>
            <a class="app-store-btn" :href="iOSAppStoreURL" target="_blank">
              <img src="../../assets/images/brand/app-store-black.svg" />
            </a>
            <a class="app-store-btn" :href="androindPlayStoreURL" target="_blank">
              <img src="../../assets/images/brand/google-play-badge.svg" />
            </a>
          </div>

          <div class="workspace-modal-form-label">
            Need more help?
            <a :href="communityURL" target="_blank" class="fts-link-primary">Click here</a>
          </div>
        </div>
      </template>
      <!-- feedback indicate -->
      <fts-feedback-indicate modal ref="ftsFeedbackIndicate" />
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn text :disabled="submitting" @click="beforeClose">
        <font-awesome-icon :icon="['fas', 'times']" />Close
      </fts-btn>

      <fts-btn color="primary" :loading="submitting" @click="submit">
        <font-awesome-icon :icon="['fas', 'check']" />Update
      </fts-btn>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";
import AvatarCropper from "vue-avatar-cropper";

import webAppConfig from "../commons/webApp.config";
import commons from "../commons/commons";
import designValidator from "../commons/designValidator";
import { authorizationStore } from "../store/authorizationStore";

import FTSClientModal from "../elements/FTSClientModal";
import FTSBtn from "../elements/FTSBtn";
import FTSFormElement from "../elements/FTSFormElement";
import FTSFeedbackIndicate from "../elements/FTSFeedbackIndicate";
import FTSSpinner from "../elements/FTSSpinner";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";

export default {
  components: {
    AvatarCropper,
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-form-element": FTSFormElement,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-spinner": FTSSpinner,
    "fts-collapsible-element": FTSCollapsibleElement,
  },
  data: () => ({
    fileUploadURL: webAppConfig.fileUploadURL,
    iOSAppStoreURL: commons.iOSAppStoreURL,
    androindPlayStoreURL: commons.androindPlayStoreURL,
    communityURL: commons.communityURL,
    sections: [
      { id: 'workspace', name: 'Workspace' },
      { id: 'company', name: 'Company' },
      { id: 'app', name: 'App' },
      { id: 'subscription', name: 'subscription' }
    ],
    section: "workspace",
    formData: {},
    originalData: null,
    resolve: null,
    loading: false,
    submitting: false,
    // uploader
    uploaderUploading: false
  }),
  validations() {
    let formData = {};
    if (this.section === 'workspace')
      formData = commons.getFormValidators(this.workspaceFields);
    else if (this.section === 'company')
      formData = commons.getFormValidators(this.fields);

    return { formData };
  },
  methods: {
    async show(item) {
      if (item && item.section) {
        const findIndex = this.sections.findIndex(x => x.id === item.section);
        if (findIndex > -1) {
          this.section = item.section;
        }
      }

      this.loading = true;

      return new Promise(async resolve => {
        this.resolve = resolve;
        this.$refs.ftsClientModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    }
                  }
                ]
              });
            } else {
              this.reset();
            }
          }
        });

        let res = await this.$store.dispatch("retrieveWorkspaceInformation");
        this.formData = res.data;
        this.loading = false;
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    beforeClose() {
      this.$refs.ftsClientModal.beforeClose();
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsClientModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    // uploader handler
    openUploader() {
      this.$refs.avatarCropper.pickImage();
    },
    uploaderUploadingHandler(form, xhr) {
      this.uploaderUploading = true;
    },
    uploaderCompletedHandler(res, form, xhr) {
      this.uploaderUploading = false;
    },
    uploaderErrorHandler(msg, type, xhr) {
      this.$toasted.error(`${msg}`);
    },
    uploaderUploadedHandler(res, form, xhr) {
      this.$set(this.formData, "companyLogo", res.url);
    },
    removeUploadPicture() {
      this.$delete(this.formData, "companyLogo");
    },
    async submit() {
      this.submitting = true;
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        this.$toasted.global.invalidFormValidate();
      } else {
        try {
          await this.$store.dispatch(
            "updateWorkspaceInformation",
            this.formData
          );
          this.$toasted.success(`Workspace is updated successfully`);
          this.reset();
        } catch (err) {
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            response: err.response
          });
        }
      }
      this.submitting = false;
    }
  },
  computed: {
    workspaceId() {
      return this.$store.getters.workspaceId;
    },
    workspaceNameSpecialCharacters() {
      return designValidator.workspaceNameSpecialCharacters.split("");
    },
    emailFields() {
      return [
        {
          type: 0,
          fieldName: "emailUser"
        },
        {
          input: "password",
          fieldName: "emailPassword"
        },
        {
          type: 0,
          fieldName: "emailSMTPServer",
          displayName: "Email SMTP Server"
        },
        {
          type: 0,
          fieldName: "emailSender"
        },
        {
          type: 0,
          fieldName: "emailSenderName"
        },
        {
          type: 2,
          fieldName: "emailPortNumber"
        },
        {
          type: 1,
          fieldName: "emailEnableSsl",
          displayName: "Email Enable SSL"
        }
      ];
    },
    workspaceFields() {
      return [
        {
          type: 0,
          fieldName: "name",
          displayName: "Workspace Name",
          required: true,
          validator: {
            format: designValidator.workspaceNameFormat
          }
        }  
      ]
    },
    fields() {
      return [
        {
          type: 0,
          fieldName: "companyName"
        },
        {
          type: 0,
          fieldName: "addressLine1",
          displayName: "Address Line 1"
        },
        {
          type: 0,
          fieldName: "addressLine2",
          displayName: "Address Line 2"
        },
        {
          type: 0,
          fieldName: "addressLine3",
          displayName: "Address Line 3"
        },
        {
          type: 0,
          fieldName: "postcode"
        },
        {
          type: 0,
          fieldName: "state"
        },
        {
          type: 0,
          fieldName: "country"
        },
        {
          type: 0,
          fieldName: "registrationNumber",
          displayName: "Business/Company Registration Number"
        },
        {
          type: 25,
          fieldName: "email"
        },
        {
          type: 26,
          fieldName: "telephoneNumber"
        }
      ];
    },
    profilePhoto() {
      if (this.formData.companyLogo) {
        const encodeURL = `/api/d/${this.workspaceId}/s${encodeURIComponent(
          this.formData.companyLogo
        )}`;
        return webAppConfig.baseURL
          ? `${webAppConfig.baseURL}${encodeURL}`
          : encodeURL;
      } else {
        return commons.defaultProfileImage;
      }
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isFooter() {
      return !(['app', 'subscription'].some(x => x === this.section));
    },
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";
.fts-workspace-modal {
  .workspace-modal__body {
    padding-top: 0 !important;
  }
  /* tabs */
  .user-profile-tabs {
    @include user-select-none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: sticky;
    top: 0;
    margin: 0 -1rem 0.375rem;
    padding: 1rem;
    background-color: var(--fts-background);
    z-index: 2;
    .user-profile-tab {
      padding-bottom: 0.175rem;
      border-bottom: 2px solid transparent;
      font-weight: 500;
      transition: all 0.3s ease-in-out;
      cursor: pointer;
      text-transform: capitalize;
      &:not(:last-child) {
        margin-right: 1rem;
      }
      &:not(.active) {
        @include theme() {
          color: rgba(theme-get("fts-gray-5"), 0.65);
        }
      }
      &:hover {
        @include theme() {
          color: rgba(theme-get("fts-gray-5"), 0.85);
        }
      }
      &.active {
        @include theme() {
          border-color: rgba(theme-get("fts-gray-5"), 0.75);
        }
        color: var(--fts-gray-5);
      }
    }
  }
  /* utilities */
  .workspace-modal-form-title {
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-dark);
    .workspace-modal-form-title--mb {
      margin-bottom: .5rem;
    }
  }
  .workspace-modal-form-label {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-dark);
  }
  /* collapsible */
  .workspace-modal-collapsible {
    .workspace-modal-collapsible__toggle {
      @extend .workspace-modal-form-title;
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      user-select: none;
      &.workspace-modal-collapsible__toggle--expand {
        .workspace-modal-collapsible__toggle-icon {
          transform: rotate(90deg);
        }
      }
      .workspace-modal-collapsible__toggle-icon {
        margin-left: .75rem;
        transition: transform .3s;
      }
    }
    .workspace-modal-collapsible__content {
      margin-top: .5rem;
    }
  }
  /* profile */
  .profile-photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    .profile-photo-container {
      position: relative;
      width: 100%;
      .profile-photo-wrapper {
        @include flex-center-middle;
        height: 150px;
        width: 100%;
        margin-bottom: 0.75rem;
        padding: 0.75rem;
        border-radius: 0.575rem;
        background: var(--fts-gray-1);
        overflow: hidden;
        cursor: pointer;
        > img {
          height: 100%;
        }
      }
      .profile-photo-icon {
        position: absolute;
        bottom: 4px;
        right: -8px;
        padding: 0.135rem;
        border-radius: 50%;
        background: #fff;
      }
    }
  }
  /* avatar */
  .avatar-cropper {
    .avatar-cropper-overlay {
      margin-top: 55px;
      align-items: flex-start;
    }
    .avatar-cropper-close {
      text-decoration: none;
    }
    .avatar-cropper-container {
      .avatar-cropper-image-container {
        /* Circle Crop */
        .cropper-crop-box {
          //border-radius: 50%;
          .cropper-view-box {
            border-radius: inherit;
            box-shadow: 0 0 0 1px #39f;
            outline: 0;
          }
          .cropper-face {
            background-color: inherit;
          }
          .cropper-dashed,
          .cropper-point.point-se,
          .cropper-point.point-sw,
          .cropper-point.point-nw,
          .cropper-point.point-ne,
          .cropper-line {
            display: none;
          }
          .cropper-view-box {
            outline: inherit;
          }
        }
      }
      .avatar-cropper-footer {
        .avatar-cropper-btn {
          &:hover {
            background-color: var(--fts-primary);
          }
        }
      }
    }
  }
  /* plan */
  .plan-description {
    margin-bottom: 1rem;
  }
  .plan-wrapper {
    display: flex;
    align-items: flex-end;
    margin-bottom: 1rem;
    padding: 1rem;
    background-color: var(--fts-gray-1);
    border-radius: 0.575rem;
    .plan-info {
      flex: 1;
      margin-right: 1rem;
      .plan-name {
        margin-bottom: 0.175rem;
        font-size: 1.25rem;
        font-weight: 600;
      }
      .plan-price {
        display: flex;
        align-items: baseline;
        .plan-price-tag {
          margin-right: 0.375rem;
          font-size: 1.75rem;
          font-weight: 600;
          color: var(--fts-primary);
        }
        .plan-price-label {
          font-size: 1rem;
        }
      }
    }
  }
  /* workspace section */
  .workspace-section {
    padding-bottom: 1rem;
  }
  /* app section */
  .app-section {
    padding-bottom: 1rem;
    .app-store-btn {
      height: 2.75rem;
      display: inline-flex;
      margin-top: 1.5rem;
      margin-bottom: 3rem;
      &:not(:first-child) {
        margin-left: .375rem;
      }
      > img {
        height: inherit;
      }
    }
  }
}
</style>